import { useRef, useState } from 'react'
import { get } from '../../../../../utils/Api'
import { senderIdDisplayUrl, uploadSenderId } from '../../../../../components/senderId/senderIdApi'
import { senderFieldsComplete, senderIdRequired, senderMatchesAccountOwner } from '../../../../../utils/senderIdentity'
import { useBooking } from './ctx'
import { STATE_NAMES, lgasFor } from './constants'
import { Combo, Field, Ic, Note, OptionCards, SelectField } from './fields'
import type { ComboOption } from './fields'
import { dialCode, countryName } from './payload'
import {
  dayLabel,
  formKind,
  hasLine2,
  isIntl,
  kindUpper,
  line1Max,
  minPickup,
  nextPickupDays,
  ownerContact,
  ownerSide,
  partyCountry,
  senderForId,
  toISO,
} from './rules'
import type { Who } from './types'

const MAX_ID_BYTES = 3 * 1024 * 1024

function AddressBlock({ who }: { who: Who }) {
  const { f, set, err, clearErr, countries, notify } = useBooking()
  const P = f[who]
  const cc = partyCountry(f, who)
  const k = formKind(f)
  const dom = k === 'domestic'
  const routeState = who === 'sender' ? f.fromState : f.toState
  const id = `${who}-addr`
  const max = line1Max(f, who)
  const l2 = hasLine2(f, who)

  const [q, setQ] = useState('')
  const [opts, setOpts] = useState<ComboOption<any>[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<any>(null)
  const seq = useRef(0)

  const search = (text: string) => {
    setQ(text)
    clearTimeout(timer.current)
    if (text.trim().length < 3) return setOpts([])
    timer.current = setTimeout(() => {
      const mine = ++seq.current
      setLoading(true)
      get(`utils/address-autocomplete?address=${encodeURIComponent(text.trim())}&country=${cc}`)
        .then((result: any) => {
          if (mine !== seq.current) return
          setOpts(
            result.status === 200
              ? (result.data?.suggests || []).map((s: any) => ({
                  key: s.placeId,
                  title: s.mainText || s.address,
                  sub: s.mainText ? s.address : '',
                  value: s,
                }))
              : [],
          )
        })
        .catch(() => setOpts([]))
        .finally(() => mine === seq.current && setLoading(false))
    }, 350)
  }

  const pick = (o: ComboOption<any>) => {
    setLoading(true)
    get(`utils/place-detail-query?mainText=${encodeURIComponent(o.value.mainText || '')}&placeId=${encodeURIComponent(o.value.placeId)}`)
      .then((result: any) => {
        if (result.status !== 200) return notify('error', result.message || 'Couldn’t load that address')
        const a = result.data || {}
        const addr = {
          l1: String(a.address || '').trim(),
          l2: '',
          // Nigerian lookups return the state as the city; domestic wants the LGA
          city: dom ? '' : a.city || '',
          state: dom ? routeState : a.state || '',
          pc: a.postCode || '',
        }
        const missing = !addr.l1 || !addr.city || !addr.state || !addr.pc || addr.l1.length > max
        set((d) => {
          d[who].addr = addr
          d[who].addrMode = missing ? 'manual' : 'confirmed'
        })
        clearErr(`${id}-q`)
        setQ('')
        setOpts([])
        if (missing) {
          // Land on the first field that still needs the agent
          setTimeout(() => {
            const first = !addr.l1 || addr.l1.length > max ? 'l1' : !addr.city ? 'city' : !addr.state ? 'state' : 'pc'
            document.getElementById(`${id}-${first}`)?.focus()
          }, 0)
        } else setTimeout(() => document.getElementById(`${id}-edit`)?.focus(), 0)
      })
      .catch(() => notify('error', 'Couldn’t load that address'))
      .finally(() => setLoading(false))
  }

  const setAddr = (key: keyof typeof P.addr, v: string) => {
    set((d) => void (d[who].addr[key] = v))
    clearErr(`${id}-${key}`, `${id}-q`)
  }

  if (P.addrMode === 'confirmed') {
    return (
      <div className='bk-pcard'>
        <span className='oi'>
          <Ic name='map-pin' />
        </span>
        <div className='pc-b'>
          <b>{P.addr.l1}</b>
          <p>{[P.addr.l2, P.addr.city, P.addr.state, P.addr.pc].filter(Boolean).join(', ')}</p>
        </div>
        <button
          type='button'
          className='bk-btn quiet sm'
          id={`${id}-edit`}
          onClick={() => set((d) => void (d[who].addrMode = 'manual'))}
        >
          Edit<span className='bk-sr'> address</span>
        </button>
      </div>
    )
  }

  if (P.addrMode === 'manual') {
    const lgas = dom ? lgasFor(routeState) : []
    return (
      <>
        <Field
          id={`${id}-l1`}
          label='Address line 1'
          hint={`House number and street — up to ${max} characters${l2 ? '. Put the rest in line 2.' : '.'}`}
          value={P.addr.l1}
          maxLength={Math.max(max, P.addr.l1.length)}
          error={err(`${id}-l1`)}
          onChange={(v) => setAddr('l1', v)}
        />
        {l2 && (
          <Field
            id={`${id}-l2`}
            label='Address line 2'
            optional
            hint='Estate, building, floor, landmark'
            value={P.addr.l2}
            onChange={(v) => setAddr('l2', v)}
          />
        )}
        <div className='bk-row c3'>
          {lgas.length ? (
            <SelectField
              id={`${id}-city`}
              label='City / LGA'
              value={P.addr.city}
              options={[...new Set([...(P.addr.city && !lgas.includes(P.addr.city) ? [P.addr.city] : []), ...lgas])].map((s) => ({ value: s, label: s }))}
              placeholder='Select LGA'
              error={err(`${id}-city`)}
              onChange={(v) => setAddr('city', v)}
            />
          ) : (
            <Field
              id={`${id}-city`}
              label={dom ? 'City / LGA' : 'City / town'}
              value={P.addr.city}
              error={err(`${id}-city`)}
              onChange={(v) => setAddr('city', v)}
            />
          )}
          {dom ? (
            <Field id={`${id}-state`} label='State' hint='From step 1' value={routeState} readOnly onChange={() => {}} error={err(`${id}-state`)} />
          ) : cc === 'NG' ? (
            <SelectField
              id={`${id}-state`}
              label='State'
              value={P.addr.state}
              options={STATE_NAMES.map((s) => ({ value: s, label: s }))}
              placeholder='Select state'
              error={err(`${id}-state`)}
              onChange={(v) => setAddr('state', v)}
            />
          ) : (
            <Field
              id={`${id}-state`}
              label='State / county'
              value={P.addr.state}
              error={err(`${id}-state`)}
              onChange={(v) => setAddr('state', v)}
            />
          )}
          <Field id={`${id}-pc`} label='Postcode' value={P.addr.pc} error={err(`${id}-pc`)} onChange={(v) => setAddr('pc', v)} />
        </div>
        <button type='button' className='bk-linkbtn' onClick={() => set((d) => void (d[who].addrMode = 'search'))}>
          <Ic name='search' /> Search for the address instead
        </button>
      </>
    )
  }

  return (
    <>
      <Combo
        id={`${id}-q`}
        label='Address'
        hint='Start typing the street or building, then pick from the list'
        value={q}
        onInput={search}
        options={opts}
        loading={loading}
        placeholder='e.g. 12 Admiralty Way'
        error={err(`${id}-q`)}
        emptyText={
          q.trim().length >= 3 ? (
            <>
              No matches in {countryName(countries, cc)}.{' '}
              <button
                type='button'
                className='bk-linkbtn inline'
                onClick={() => set((d) => void (d[who].addrMode = 'manual'))}
              >
                Enter it manually
              </button>
            </>
          ) : (
            ''
          )
        }
        onPick={pick}
      />
      <button
        type='button'
        className='bk-linkbtn'
        onClick={() => {
          set((d) => {
            d[who].addrMode = 'manual'
            if (dom) d[who].addr.state = routeState
          })
          clearErr(`${id}-q`)
        }}
      >
        <Ic name='pencil' /> Enter the address manually
      </button>
    </>
  )
}

function SenderIdBlock() {
  const { f, set, err, clearErr, customer, notify } = useBooking()
  const [busy, setBusy] = useState(false)
  if (!customer) return null
  const s = senderForId(f, customer)
  const required = senderIdRequired(s, customer as any, kindUpper(f))
  if (!required) return null
  const mismatch = senderFieldsComplete(s) && !senderMatchesAccountOwner(s, customer as any) && f.sender.mode !== 'other'
  const preview = senderIdDisplayUrl(f.sender.idImage, f.sender.idPreview)

  const onFile = (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) return notify('error', 'Only image files are accepted for the ID')
    if (file.size > MAX_ID_BYTES) return notify('error', 'The ID photo must be 3 MB or smaller')
    setBusy(true)
    uploadSenderId(file).then((r) => {
      if (r.ok) {
        set((d) => {
          d.sender.idImage = r.senderIdImage
          d.sender.idPreview = r.previewUrl
        })
        clearErr('sid-in')
      } else notify('error', r.message)
      setBusy(false)
    })
  }

  return (
    <fieldset className='bk-grp'>
      <legend>Sender’s ID</legend>
      <p className='bk-intro'>
        {mismatch
          ? `The sender’s name and phone don’t match ${customer.firstname} ${customer.lastname}’s account, so we need a photo of the sender’s government ID.`
          : 'Someone other than the account holder is sending this, so we need a photo of their government ID — NIN slip, driver’s licence, international passport or voter’s card.'}
      </p>
      <input
        type='file'
        accept='image/*'
        id='sid-in'
        className='bk-sr bk-file'
        aria-invalid={err('sid-in') ? true : undefined}
        aria-describedby={err('sid-in') ? 'sid-in-err' : undefined}
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {busy ? (
        <div className='bk-drop' role='status'>
          <span className='oi'>
            <Ic name='loader-circle' className='spin' />
          </span>
          <span>
            <b>Uploading…</b>
          </span>
        </div>
      ) : f.sender.idImage ? (
        <div className='bk-preview'>
          {preview ? <img src={preview} alt='Sender’s ID' /> : <span className='oi'><Ic name='id-card' /></span>}
          <div className='pv-b'>
            <b>ID uploaded</b>
            <span>Check the name and photo are readable.</span>
          </div>
          <label htmlFor='sid-in' className='bk-btn quiet sm'>
            Replace
          </label>
          <button
            type='button'
            className='bk-btn danger-quiet sm'
            onClick={() =>
              set((d) => {
                d.sender.idImage = ''
                d.sender.idPreview = null
              })
            }
          >
            Remove
          </button>
        </div>
      ) : (
        <label htmlFor='sid-in' className='bk-drop'>
          <span className='oi'>
            <Ic name='id-card' />
          </span>
          <span>
            <b>Upload ID photo</b>
            <span>Clear photo, all four corners visible. Up to 3 MB.</span>
          </span>
        </label>
      )}
      {err('sid-in') && (
        <p className='bk-err' id='sid-in-err'>
          <Ic name='circle-alert' />
          <span>{err('sid-in')}</span>
        </p>
      )}
    </fieldset>
  )
}

function HandoverBlock() {
  const { f, set, err, clearErr } = useBooking()
  const imp = formKind(f) === 'import'
  const H = f.handover
  return (
    <fieldset className='bk-grp'>
      <legend>{imp ? 'Collection' : 'How will we get it?'}</legend>
      <OptionCards
        name='ho-mode'
        legend='Collection or drop-off'
        hideLegend
        value={H.mode}
        error={err('ho-mode')}
        onChange={(v) => {
          set((d) => void (d.handover.mode = v as 'pickup' | 'dropoff'))
          clearErr('ho-mode')
        }}
        options={[
          {
            value: 'pickup',
            title: imp ? 'DHL collects from the sender' : 'Collect from the sender',
            sub: imp ? 'From the address above, on the date you choose' : 'A rider picks it up from the address above',
            icon: 'truck',
          },
          {
            value: 'dropoff',
            title: imp ? 'The sender drops it at DHL' : 'Drop-off at a station',
            sub: imp ? 'No collection date needed' : 'No date needed — it goes out on the next dispatch',
            icon: imp ? 'plane' : 'store',
          },
        ]}
      />
      {H.mode === 'pickup' && (
        <div className='bk-field'>
          <span className='bk-lbl' id='ho-date-l'>
            Collection date
          </span>
          <div className='bk-chips mb' role='radiogroup' aria-labelledby='ho-date-l'>
            {nextPickupDays(3).map((d) => (
              <label className='bk-chip' key={toISO(d)}>
                <input
                  type='radio'
                  name='ho-day'
                  checked={H.date === toISO(d)}
                  onChange={() => {
                    set((x) => void (x.handover.date = toISO(d)))
                    clearErr('ho-date')
                  }}
                />
                {dayLabel(d)}
              </label>
            ))}
          </div>
          <Field
            id='ho-date'
            label='Or choose another date'
            type='date'
            min={toISO(minPickup())}
            hint='No collections on Sundays. After 12 noon, the earliest is tomorrow.'
            value={H.date}
            error={err('ho-date')}
            onChange={(v) => {
              set((x) => void (x.handover.date = v))
              clearErr('ho-date')
            }}
          />
        </div>
      )}
    </fieldset>
  )
}

export default function PartyStep({ who }: { who: Who }) {
  const { f, set, err, clearErr, customer, countries } = useBooking()
  const P = f[who]
  const cc = partyCountry(f, who)
  const k = formKind(f)
  const ownerHere = ownerSide(f) === who && !!customer
  const field = (key: 'first' | 'last' | 'company' | 'phone' | 'email') => ({
    id: `${who}-${key}`,
    value: P[key],
    error: err(`${who}-${key}`),
    onChange: (v: string) => {
      set((d) => void (d[who][key] = v))
      clearErr(`${who}-${key}`)
    },
  })

  return (
    <>
      {ownerHere && customer && (
        <OptionCards
          name={`${who}-mode`}
          legend={who === 'sender' ? 'Who is sending it?' : 'Who is receiving it?'}
          value={P.mode}
          error={err(`${who}-mode`)}
          onChange={(v) => {
            set((d) => {
              d[who].mode = v as 'owner' | 'other'
              Object.assign(d[who], v === 'owner' ? ownerContact(customer) : { first: '', last: '', phone: '', email: '' })
              if (v === 'owner') {
                d[who].company = ''
                d[who].idImage = ''
                d[who].idPreview = null
              }
              // Start from the account's address when it has one
              if (v === 'owner' && !d[who].addr.l1 && customer.address && cc === 'NG') {
                d[who].addr = {
                  l1: String(customer.address),
                  l2: '',
                  city: k === 'domestic' ? '' : String(customer.city || ''),
                  state: k === 'domestic' ? (who === 'sender' ? d.fromState : d.toState) : String(customer.state || ''),
                  pc: '',
                }
                d[who].addrMode = 'manual'
              }
            })
            clearErr(`${who}-mode`)
          }}
          options={[
            {
              value: 'owner',
              title: 'The account holder',
              sub: `${customer.firstname} ${customer.lastname}${customer.phonenumber ? ` · ${customer.phonenumber}` : ''}`,
              icon: 'user',
            },
            {
              value: 'other',
              title: 'Someone else',
              sub: who === 'sender' ? (k === 'import' ? 'The supplier or person abroad' : 'A friend, supplier or staff member') : 'Family, a client or a business',
              icon: 'users',
            },
          ]}
        />
      )}

      {ownerHere && P.mode === 'owner' ? (
        <div className='bk-pcard mb-l'>
          <span className='av' aria-hidden='true'>
            {(P.first[0] || '') + (P.last[0] || '')}
          </span>
          <div className='pc-b'>
            <b>
              {P.first} {P.last}
            </b>
            <p>{[P.phone && `${dialCode(countries, cc)} ${P.phone}`, P.email].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
      ) : (
        (!ownerHere || P.mode === 'other') && (
          <fieldset className='bk-grp'>
            <legend>{who === 'sender' ? 'Sender’s details' : 'Receiver’s details'}</legend>
            <div className='bk-row c2'>
              <Field label='First name' {...field('first')} />
              <Field label='Last name' {...field('last')} />
            </div>
            <Field label='Company' optional {...field('company')} />
            <div className='bk-row c2'>
              <Field label='Phone number' type='tel' inputMode='tel' pre={dialCode(countries, cc) || undefined} {...field('phone')} />
              <Field
                label='Email'
                type='email'
                inputMode='email'
                optional={who === 'sender'}
                hint={who === 'sender' ? 'Leave blank to use the account email' : 'Tracking updates go here'}
                {...field('email')}
              />
            </div>
          </fieldset>
        )
      )}

      {(!ownerHere || P.mode) && (
        <fieldset className='bk-grp'>
          <legend>
            {who === 'sender' ? 'Collection address' : 'Delivery address'}{' '}
            <span className='bk-optional'>· {countryName(countries, cc)}</span>
          </legend>
          <AddressBlock who={who} />
          {P.addrMode === 'confirmed' && err(`${who}-addr-q`) && (
            <p className='bk-err' id={`${who}-addr-q-err`}>
              <Ic name='circle-alert' />
              <span>{err(`${who}-addr-q`)}</span>
            </p>
          )}
        </fieldset>
      )}

      {who === 'sender' && (k === 'export' || k === 'domestic') && <SenderIdBlock />}
      {who === 'sender' && <HandoverBlock />}
      {who === 'receiver' && isIntl(f) && (
        <Note tone='info' icon='info'>
          <p>
            Customs in {countryName(countries, cc)} may call the receiver. A correct phone number avoids delays.
          </p>
        </Note>
      )}
    </>
  )
}
